import { useState } from "react";
import { ArrowRight, CheckCircle2, ChevronRight, HelpCircle, XCircle, Zap } from "lucide-react";
import { useSystemHealthJourney, useSystemHealthSend, useSystemHealthSends } from "../../lib/api";
import {
  Badge, Crumbs, FailedToLoad, Loading, MigrationPending, Notes, Notice, PAGE, Pager,
  endpoint, focusRing, panel, pillClass, pillStyle,
} from "./healthUi";
import { formatMs, formatWhen, ownerLabel, resultLabel, resultTone } from "./healthFormat";

// Templates tab: the Service Journey, one screen per question.
//   stages -> templates in a stage -> the sends behind a number -> one send

const RESULT_FILTERS = [
  { key: "", label: "All" },
  { key: "didnt_reach", label: "Failed" },
  { key: "reached", label: "Delivered" },
  { key: "unconfirmed", label: "Unknown" },
];

// ---- shared pieces ------------------------------------------------------------


// How a send's result looks in a list: the icon, and the colours of its chip.
const RESULT_LOOKS = {
  good: { icon: CheckCircle2, soft: "#ECFDF3", solid: "#16A34A", ink: "#166534" },
  bad: { icon: XCircle, soft: "#FEF2F2", solid: "#DC2626", ink: "#991B1B" },
  idle: { icon: HelpCircle, soft: "#F3F4F6", solid: "#6B7280", ink: "#374151" },
};

const RESULT_COLOURS = { reached: "#16A34A", unconfirmed: "#CBD5E1", didnt_reach: "#DC2626" };

// One thin bar: how the sends at a stage or template ended.
function DeliveryBar({ row }) {
  if (!row.sent) return null;
  const parts = [["reached", "reached the customer"], ["unconfirmed", "delivery unknown"], ["didnt_reach", "did not reach"]];
  return (
    <span
      role="img"
      aria-label={parts.map(([key, words]) => `${row[key]} ${words}`).join(", ")}
      className="mt-2 flex h-1.5 w-full max-w-[360px] overflow-hidden rounded-full bg-[#F3F4F6]"
    >
      {parts.map(([key]) => row[key] > 0 && <span key={key} style={{ width: `${(100 * row[key]) / row.sent}%`, background: RESULT_COLOURS[key] }} />)}
    </span>
  );
}

// How long we took to start sending, averaged. "Not timed" is said, never shown as 0.
function Speed({ speed, label = "Avg speed" }) {
  const timed = speed?.avg_ms != null;
  return (
    <span className="flex min-w-[64px] flex-col items-end">
      <span className="text-[11px] font-medium uppercase tracking-[0.06em] text-[#6B7280]">{label}</span>
      <span className={`mt-0.5 text-[14px] font-semibold tabular-nums ${timed ? "text-[#111827]" : "text-[#D1D5DB]"}`}>
        {timed ? formatMs(speed.avg_ms) : "Not timed"}
      </span>
    </span>
  );
}

// A labelled number that opens the sends behind it. Zero is plain text: nothing to open.
function Figure({ label, n, onClick, name, tone }) {
  const number = "text-[14px] font-semibold tabular-nums";
  return (
    <span className="flex min-w-[64px] flex-col items-end">
      <span className="text-[11px] font-medium uppercase tracking-[0.06em] text-[#6B7280]">{label}</span>
      {n ? (
        <button
          type="button"
          aria-label={`${n} ${name}`}
          onClick={onClick}
          className={`mt-0.5 cursor-pointer rounded underline-offset-4 hover:underline ${number} ${tone ? "text-[#B91C1C]" : "text-[#111827]"} ${focusRing}`}
        >
          {n}
        </button>
      ) : (
        <span className={`mt-0.5 ${number} text-[#D1D5DB]`}>0</span>
      )}
    </span>
  );
}

const FIGURES = [
  { key: "sent", label: "Sent", result: "", words: "sent" },
  { key: "reached", label: "Delivered", result: "reached", words: "reached the customer" },
  { key: "didnt_reach", label: "Failed", result: "didnt_reach", words: "did not reach the customer", tone: true },
  { key: "unconfirmed", label: "Unknown", result: "unconfirmed", words: "delivery unknown" },
];

// ---- screen 1: stages -------------------------------------------------------

// The whole period in one strip, before the stages.
function Totals({ totals, open }) {
  const cells = [
    { label: "Sent", value: totals.sent, result: "" },
    { label: "Delivered", value: totals.reached, result: "reached", colour: "#166534" },
    { label: "Failed", value: totals.didnt_reach, result: "didnt_reach", colour: totals.didnt_reach ? "#B91C1C" : undefined },
    { label: "Unknown", value: totals.unconfirmed, result: "unconfirmed" },
  ];
  const timed = totals.speed?.avg_ms != null;
  return (
    <ul aria-label="This period" title={endpoint("journey")} className={`${panel} mt-4 grid grid-cols-2 divide-[#F3F4F6] overflow-hidden sm:grid-cols-5 sm:divide-x`}>
      {cells.map((c) => (
        <li key={c.label}>
          <button
            type="button"
            onClick={() => open({ level: "sends", result: c.result })}
            aria-label={`${c.value} ${c.label}. Show them`}
            className={`block w-full cursor-pointer px-5 py-4 text-left hover:bg-[#F9FAFB] ${focusRing}`}
          >
            <span className="block text-[12px] font-medium text-[#6B7280]">{c.label}</span>
            <span className="mt-1 block text-[22px] font-semibold tabular-nums" style={{ color: c.colour || "#111827" }}>{c.value}</span>
          </button>
        </li>
      ))}
      <li className="col-span-2 bg-[#F8FAFF] px-5 py-4 sm:col-span-1">
        <span className="block text-[12px] font-medium text-[#6B7280]">Average speed</span>
        <span className={`mt-1 block text-[22px] font-semibold tabular-nums ${timed ? "text-[#111827]" : "text-[#9CA3AF]"}`}>
          {timed ? formatMs(totals.speed.avg_ms) : "Not timed"}
        </span>
        <span className="block text-[11px] text-[#6B7280]">
          {timed ? `over ${totals.speed.timed} timed ${totals.speed.timed === 1 ? "send" : "sends"}` : "starts with new sends"}
        </span>
      </li>
    </ul>
  );
}

function Stages({ query, open }) {
  const { data, isLoading, error, refetch } = query;
  if (isLoading) return <Loading />;
  if (error) return <FailedToLoad error={error} retry={refetch} />;
  if (data?.migration_pending) return <MigrationPending />;
  const t = data.totals;
  if (!t.sent) {
    return <Notice>Nothing was sent in this period. Try a longer period. Sends are only recorded from when this feature went live.</Notice>;
  }
  const active = data.stages.filter((s) => s.sent > 0);
  const quiet = data.stages.filter((s) => s.sent === 0 && s.key);
  return (
    <>
      <Notes notes={data.notes} />
      <Totals totals={t} open={open} />
      <section title={endpoint("journey")} className={`${panel} mt-5 p-5 sm:p-6`}>
        <h2 className="text-[18px] font-semibold text-[#111827]">Service journey</h2>
        <ol aria-label="Service journey" className="mt-4">
          {active.map((stage, i) => {
            const toTemplates = () => open({ level: "templates", stage: stage.key, stageLabel: stage.label });
            const go = (result) => () => open({ level: "sends", stage: stage.key, stageLabel: stage.label, result });
            const last = i === active.length - 1;
            return (
              <li key={stage.key || "none"} className="flex gap-4">
                <span aria-hidden="true" className="flex w-6 flex-none flex-col items-center">
                  <span
                    className="mt-4 flex h-6 w-6 flex-none items-center justify-center rounded-full text-[11px] font-semibold text-white"
                    style={{ background: stage.didnt_reach ? "#DC2626" : "#2667B5" }}
                  >
                    {i + 1}
                  </span>
                  {!last && <span className="w-px flex-1 bg-[#E5E7EB]" />}
                </span>
                <div className={`flex min-w-0 flex-1 flex-wrap items-center justify-between gap-x-6 gap-y-3 py-3 ${last ? "" : "border-b border-[#F3F4F6]"}`}>
                  <div className="min-w-0 flex-1 basis-52">
                    <button
                      type="button"
                      onClick={toTemplates}
                      className={`cursor-pointer rounded text-left text-[15px] font-semibold text-[#111827] hover:underline ${focusRing}`}
                    >
                      {stage.label}
                    </button>
                    <span className="block text-[12px] text-[#6B7280]">
                      {stage.templates.length} {stage.templates.length === 1 ? "template" : "templates"}
                    </span>
                    <DeliveryBar row={stage} />
                  </div>
                  <div className="flex flex-wrap items-start gap-x-4 gap-y-2">
                    {FIGURES.map((f) => (
                      <Figure key={f.key} label={f.label} n={stage[f.key]} onClick={go(f.result)} name={`${f.words} at ${stage.label}`} tone={f.tone} />
                    ))}
                    <Speed speed={stage.speed} />
                  </div>
                </div>
              </li>
            );
          })}
        </ol>
        <p className="mt-4 flex flex-wrap gap-x-5 gap-y-1 border-t border-[#F3F4F6] pt-3 text-[12px] text-[#4B5563]">
          <span className="flex items-center gap-1.5"><span className="h-2 w-2 rounded-full" style={{ background: RESULT_COLOURS.reached }} /> Delivered</span>
          <span className="flex items-center gap-1.5"><span className="h-2 w-2 rounded-full" style={{ background: RESULT_COLOURS.unconfirmed }} /> Unknown</span>
          <span className="flex items-center gap-1.5"><span className="h-2 w-2 rounded-full" style={{ background: RESULT_COLOURS.didnt_reach }} /> Failed</span>
        </p>
        {quiet.length > 0 && (
          <p className="mt-2 text-[12px] leading-5 text-[#6B7280]">
            Nothing sent at {quiet.length} stages: {quiet.map((s) => s.label).join(", ")}.
          </p>
        )}
      </section>
    </>
  );
}

// ---- screen 2: templates in a stage -----------------------------------------

function StageTemplates({ query, view, open }) {
  const { data, isLoading, error, refetch } = query;
  if (isLoading) return <Loading />;
  if (error) return <FailedToLoad error={error} retry={refetch} />;
  const stage = data?.stages?.find((s) => s.key === view.stage);
  if (!stage || !stage.templates.length) return <Notice>No templates were sent at this stage in this period. Try a longer period.</Notice>;
  return (
    <section title={endpoint("journey")} className="mt-4">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <h2 className="text-[18px] font-semibold text-[#111827]">{stage.label}: templates sent</h2>
        <Speed speed={stage.speed} label="Stage avg speed" />
      </div>
      <ul aria-label="Templates" className="mt-4 flex flex-col gap-3">
        {stage.templates.map((t) => {
          const go = (result, reason) => () =>
            open({
              level: "sends", stage: stage.key, stageLabel: stage.label, template: t.template, result,
              errorCode: reason?.code, errorLabel: reason?.meaning,
            });
          return (
            <li key={t.template} className={`${panel} p-5`}>
              <div className="flex flex-wrap items-center justify-between gap-x-6 gap-y-3">
                <div className="min-w-0 flex-1 basis-52">
                  <button
                    type="button"
                    onClick={go("")}
                    className={`cursor-pointer rounded text-left font-mono text-[14px] font-semibold text-[#111827] hover:underline ${focusRing}`}
                  >
                    {t.template}
                  </button>
                  <DeliveryBar row={t} />
                </div>
                <div className="flex flex-wrap items-start gap-x-4 gap-y-2">
                  {FIGURES.map((f) => (
                    <Figure key={f.key} label={f.label} n={t[f.key]} onClick={go(f.result)} name={`${f.words} for ${t.template}`} tone={f.tone} />
                  ))}
                  <Speed speed={t.speed} />
                </div>
              </div>
              {t.reasons.length > 0 && (
                <div className="mt-4 rounded-xl bg-[#FEF2F2] px-4 py-3">
                  <p className="text-[12px] font-semibold uppercase tracking-[0.06em] text-[#991B1B]">Failure reasons</p>
                  <ul className="mt-1 flex flex-col">
                    {t.reasons.map((r) => (
                      <li key={`${r.code}-${r.meaning}`} className="text-[13px] leading-5">
                        <button type="button" onClick={go("didnt_reach", r)} className={`cursor-pointer rounded py-1 text-left text-[#111827] hover:underline ${focusRing}`}>
                          <span className="font-semibold tabular-nums text-[#B91C1C]">{r.count}</span> {r.meaning}{" "}
                          <span className="text-[#6B7280]">({ownerLabel(r.owner)} fixes it)</span>
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </li>
          );
        })}
      </ul>
    </section>
  );
}

// ---- screen 3: the sends ----------------------------------------------------

function Sends({ scope, range, view, open }) {
  const [result, setResult] = useState(view.result || "");
  const [errorCode, setErrorCode] = useState(view.errorCode || "");
  const [page, setPage] = useState(view.page || 0);
  const { data, isLoading, error, refetch } = useSystemHealthSends({
    ...scope,
    from: range.from(),
    stage: view.stage,
    template: view.template,
    result,
    errorCode,
    limit: PAGE,
    offset: page * PAGE,
  });
  // Choosing a filter starts a fresh list, so a reason picked earlier is dropped
  // with it rather than silently combined.
  const pick = (key) => {
    setResult(key);
    setErrorCode("");
    setPage(0);
  };
  return (
    <>
    <Notes notes={data?.notes} />
    <section title={endpoint("sends")} className={`${panel} mt-4 p-5 sm:p-6`}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="text-[12px] font-medium uppercase tracking-[0.06em] text-[#6B7280]">{view.template ? "Template" : "Sends"}</p>
          <h2 className={`mt-0.5 break-all font-semibold text-[#111827] ${view.template ? "font-mono text-[17px]" : "text-[18px]"}`}>
            {view.template || view.stageLabel || "All sends"}
          </h2>
          {data && <p className="mt-0.5 text-[13px] text-[#6B7280]">{data.total} {data.total === 1 ? "send" : "sends"}</p>}
        </div>
        <div className="flex flex-wrap gap-1 rounded-xl bg-[#F3F4F6] p-1" role="group" aria-label="Show">
          {RESULT_FILTERS.map((f) => {
            const on = result === f.key && !errorCode;
            return (
              <button
                key={f.key}
                type="button"
                aria-pressed={on}
                onClick={() => pick(f.key)}
                className={`h-9 cursor-pointer rounded-lg px-3 text-[13px] font-medium ${on ? "bg-white text-[#111827] shadow-[0_1px_2px_rgba(16,24,40,0.08)]" : "text-[#4B5563] hover:text-[#111827]"} ${focusRing}`}
              >
                {f.label}
              </button>
            );
          })}
        </div>
      </div>
      {errorCode && (
        <button
          type="button"
          onClick={() => setErrorCode("")}
          aria-label={`Remove the filter: ${view.errorLabel || errorCode}`}
          className={`${pillClass} mt-3`}
          style={pillStyle(true)}
        >
          Reason: {view.errorLabel || errorCode} ×
        </button>
      )}
      {isLoading ? (
        <Loading />
      ) : error ? (
        <FailedToLoad error={error} retry={refetch} />
      ) : data.total === 0 ? (
        <p className="mt-4 text-[13px] text-[#4B5563]">
          No sends match these filters.{" "}
          <button type="button" onClick={() => pick("")} className={`cursor-pointer rounded font-semibold text-[#2667B5] underline ${focusRing}`}>
            Show all sends
          </button>
        </p>
      ) : (
        <>
          <ul aria-label="Sends" className="mt-4 divide-y divide-[#F3F4F6] border-t border-[#F3F4F6]">
            {data.rows.map((row) => {
              const toSend = () => open({ level: "send", id: row.id, from: { ...view, result, errorCode, page } });
              const look = RESULT_LOOKS[resultTone(row.result)] || RESULT_LOOKS.idle;
              const Icon = look.icon;
              return (
                <li key={row.id}>
                  <button
                    type="button"
                    onClick={toSend}
                    className={`-mx-2 flex w-[calc(100%+16px)] cursor-pointer items-center gap-4 rounded-xl px-2 py-3.5 text-left hover:bg-[#F9FAFB] ${focusRing}`}
                  >
                    <span className="flex h-10 w-10 flex-none items-center justify-center rounded-xl" style={{ background: look.soft, color: look.solid }}>
                      <Icon size={19} aria-hidden="true" />
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="block text-[14px] font-semibold text-[#111827]">{row.group_name || "Open"}</span>
                      <span className="block text-[12px] text-[#6B7280]">
                        {[row.workshop, formatWhen(row.occurred_at), !view.template && row.template].filter(Boolean).join(" · ")}
                        {row.history && <span className="ml-2 rounded bg-[#F3F4F6] px-1.5 py-0.5 text-[11px] text-[#4B5563]">From the activity log</span>}
                      </span>
                      {row.reason?.meaning && <span className="mt-1 block text-[13px] text-[#991B1B]">{row.reason.meaning}</span>}
                    </span>
                    <span className="flex flex-none flex-col items-end gap-1">
                      <span className="rounded-full px-2.5 py-1 text-[12px] font-semibold" style={{ background: look.soft, color: look.ink }}>
                        {resultLabel(row.result)}
                      </span>
                      <span className={`inline-flex items-center gap-1 text-[12px] tabular-nums ${row.reaction_ms != null ? "font-semibold text-[#111827]" : "text-[#9CA3AF]"}`}>
                        <Zap size={12} aria-hidden="true" className={row.reaction_ms != null ? "text-[#2667B5]" : "text-[#D1D5DB]"} />
                        {row.reaction_ms != null ? formatMs(row.reaction_ms) : "Not timed"}
                      </span>
                    </span>
                    <ChevronRight size={16} aria-hidden="true" className="hidden flex-none text-[#9CA3AF] sm:block" />
                  </button>
                </li>
              );
            })}
          </ul>
          <Pager page={page} shown={data.rows.length} total={data.total} onPage={setPage} />
        </>
      )}
    </section>
    </>
  );
}

// ---- screen 4: one send -----------------------------------------------------

function Field({ label, children }) {
  return (
    <div>
      <div className="text-[11px] font-semibold uppercase tracking-[0.06em] text-[#6B7280]">{label}</div>
      <div className="mt-0.5 text-[13px] text-[#111827]">{children || "–"}</div>
    </div>
  );
}

function SendDetail({ id }) {
  const { data: send, isLoading, error, refetch } = useSystemHealthSend(id);
  if (isLoading) return <Loading />;
  if (error) return <FailedToLoad error={error} retry={refetch} />;
  const reason = send.reason;
  return (
    <div title={endpoint(`sends/${id}`)} className="mt-4 flex flex-col gap-6">
      <section className={`${panel} p-6`}>
        <div className="flex flex-wrap items-center justify-between gap-3">
          <h2 className="text-[18px] font-semibold text-[#111827]">{send.group_name || "Send"}</h2>
          <Badge label={resultLabel(send.result)} tone={resultTone(send.result)} />
        </div>
        <div className="mt-4 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          <Field label="Sent at">{formatWhen(send.occurred_at)}</Field>
          <Field label="Workshop">{send.workshop}</Field>
          <Field label="Journey stage">{send.stage_label}</Field>
          <Field label="Template"><span className="font-mono text-[12px]">{send.template}</span></Field>
          <Field label="Customer phone">{send.recipient_last4 ? `ending ${send.recipient_last4}` : ""}</Field>
        </div>
      </section>

      {!send.history && send.chat360_ms != null && (
        <section className={`${panel} p-6`}>
          <h2 className="text-[16px] font-semibold text-[#111827]">Timing</h2>
          <ol className="mt-4 flex flex-wrap items-stretch gap-3">
            <li className="flex-1 basis-48 rounded-xl bg-[#F8FAFF] px-4 py-3">
              <span className="flex items-center gap-1 text-[12px] font-medium text-[#6B7280]"><Zap size={13} aria-hidden="true" className="text-[#2667B5]" /> Our speed</span>
              <span className={`mt-1 block text-[20px] font-semibold tabular-nums ${send.reaction_ms != null ? "text-[#111827]" : "text-[#9CA3AF]"}`}>
                {send.reaction_ms != null ? formatMs(send.reaction_ms) : "Not timed"}
              </span>
            </li>
            <li aria-hidden="true" className="hidden items-center text-[#9CA3AF] sm:flex"><ArrowRight size={18} /></li>
            <li className="flex-1 basis-48 rounded-xl bg-[#F9FAFB] px-4 py-3">
              <span className="block text-[12px] font-medium text-[#6B7280]">Chat360's answer</span>
              <span className="mt-1 block text-[20px] font-semibold tabular-nums text-[#111827]">{formatMs(send.chat360_ms)}</span>
            </li>
          </ol>
        </section>
      )}

      {reason && (
        <section className="rounded-2xl border border-[#FECACA] bg-[#FEF2F2] p-6">
          <h2 className="text-[16px] font-semibold text-[#991B1B]">Failure reason</h2>
          <p className="mt-2 text-[14px] text-[#111827]">{reason.meaning}</p>
          <p className="mt-2 text-[13px] text-[#374151]">
            <strong>Who fixes it:</strong> {ownerLabel(reason.owner)}. {reason.next_step}
          </p>
        </section>
      )}

      <section className={`${panel} p-6`}>
        <h2 className="text-[16px] font-semibold text-[#111827]">Timeline</h2>
        <ol className="mt-4 flex flex-col gap-3">
          {send.timeline.map((step, i) => (
            <li key={`${step.what}-${i}`} className="flex items-start gap-3 text-[13px]">
              <span className="mt-1.5 h-2 w-2 flex-none rounded-full" style={{ background: step.ok ? "#16A34A" : "#DC2626" }} />
              <span className="w-[110px] flex-none text-[#4B5563]">{formatWhen(step.at)}</span>
              <span className="text-[#111827]">
                {step.what}
                {!step.ok && <span className="ml-2 font-semibold text-[#B91C1C]">Problem</span>}
              </span>
            </li>
          ))}
        </ol>
        {send.result === "unconfirmed" && (
          <p className="mt-4 text-[12px] leading-5 text-[#4B5563]">
            {send.history
              ? "This send is taken from the activity log. It was sent, but Chat360's answer and WhatsApp's delivery report were not recorded."
              : send.delivery_reported
                ? "WhatsApp has not reported on this message yet."
                : "WhatsApp does not report back on this kind of message, so we can only confirm that Chat360 accepted it."}
          </p>
        )}
      </section>

      {!send.history && (
      <details className={`${panel} p-6`}>
        <summary className={`cursor-pointer rounded text-[14px] font-semibold text-[#111827] ${focusRing}`}>Technical details</summary>
        <div className="mt-4 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          <Field label="Chat360 call">{send.operation}</Field>
          <Field label="Answer">{send.http_status ? `HTTP ${send.http_status}` : ""}</Field>
          <Field label="Took">{send.duration_ms != null ? `${send.duration_ms} ms` : ""}</Field>
          <Field label="Attempt">{send.attempt}</Field>
          <Field label="Message ID"><span className="break-all font-mono text-[11px]">{send.message_id}</span></Field>
          <Field label="Error code">
            <span className="font-mono text-[12px]">{reason ? `${reason.code}${reason.detail ? `: ${reason.detail}` : ""}` : ""}</span>
          </Field>
        </div>
      </details>
      )}
    </div>
  );
}

// ---- the tab ----------------------------------------------------------------

const STAGES_VIEW = { level: "stages" };

export default function Journey({ scope, range }) {
  const [view, setView] = useState(STAGES_VIEW);
  const query = useSystemHealthJourney({ ...scope, from: range.from() });
  // So a recorder that has gone quiet is visible: "nothing sent" and "nothing recorded" look the same otherwise.
  const lastCall = query.data?.recording?.last_call_at;
  const lastRecorded = lastCall ? formatWhen(lastCall) : "";

  // Each crumb goes back to the screen it names, so the trail is the back button.
  // "All stages" has no stage of its own, so a list opened from the total skips that crumb.
  const at = view.level === "send" ? view.from : view;
  const toStages = () => setView(STAGES_VIEW);
  const toTemplates = () => setView({ level: "templates", stage: at.stage, stageLabel: at.stageLabel });
  const trail = [{ label: "All stages", go: view.level === "stages" ? undefined : toStages }];
  if (view.level !== "stages" && at.stage !== undefined) {
    trail.push({ label: at.stageLabel, go: view.level === "templates" ? undefined : toTemplates });
  }
  if (view.level === "sends") trail.push({ label: view.template || "Sends" });
  if (view.level === "send") {
    trail.push({ label: at.template || "Sends", go: () => setView(view.from) });
    trail.push({ label: "One send" });
  }

  return (
    <>
      <Crumbs trail={trail} period={lastRecorded ? `${range.label} · last send recorded ${lastRecorded}` : range.label} />
      {view.level === "stages" && <Stages query={query} open={setView} />}
      {view.level === "templates" && <StageTemplates query={query} view={view} open={setView} />}
      {view.level === "sends" && <Sends key={JSON.stringify(view)} scope={scope} range={range} view={view} open={setView} />}
      {view.level === "send" && <SendDetail id={view.id} />}
    </>
  );
}
